import React from 'react';
import { ExportButton } from '../ExportButton';

// Player base interface
interface PlayerBase {
  playerNumber: string;
  playerName: string;
  playerId: string;
  position?: string | string[];
  gamesPlayed?: number;
}

interface PlayersReportTableProps<T extends PlayerBase> {
  title: string;
  columns: {
    header: string;
    key: keyof T | 'special';
    render?: (player: T) => React.ReactNode;
    sortable?: boolean;
  }[];
  players: T[];
  sortConfig?: {
    key: string;
    direction: 'asc' | 'desc';
  } | null;
  onSortChange?: (sortConfig: { key: string; direction: 'asc' | 'desc' } | null) => void;
  showExport?: boolean;
  teamName?: string;
  seasonName?: string;
  clientId?: string | null;
}

function PlayersReportTable<T extends PlayerBase>({
  title,
  columns,
  players,
  sortConfig,
  onSortChange,
  showExport = false,
  teamName,
  seasonName,
  clientId,
}: PlayersReportTableProps<T>) {
  const toSortableNumber = (value: unknown): number | null => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') return null;

    const cleaned = value.trim().replace(/,/g, '').replace(/%$/, '');
    if (cleaned === '') return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Sort players based on current sort configuration
  const sortedPlayers = React.useMemo(() => {
    if (!sortConfig) return players;

    return [...players].sort((a, b) => {
      const { key, direction } = sortConfig;

      // Find the column configuration
      const column = columns.find((col) => {
        if (col.key === 'special') {
          if (col.header === 'Player') return key === 'player';
          if (col.header === 'Position') return key === 'position';
          if (col.header === 'Games Played' || col.header === 'GP') return key === 'gamesPlayed';
          if (col.header === 'Goals +/-' || col.header === 'G+/-') return key === 'goalsPlusMinus';
          if (col.header === 'PPG') return key === 'PPG';
          if (col.header === 'GPG') return key === 'GPG';
          if (col.header === 'TO/PG') return key === 'TO/PG';
          if (col.header === 'CTO/PG') return key === 'CTO/PG';
          if (col.header === 'SPG') return key === 'SPG';
          if (col.header === 'GAPG') return key === 'GAPG';
          if (col.header === 'S%') return key === 'S%';
          if (col.header === 'W%') return key === 'W%';
          if (col.header === 'SV') return key === 'SV';
          return false;
        }
        return String(col.key) === key;
      });
      if (!column) return 0;

      let aValue: any;
      let bValue: any;

      // Handle special rendering columns
      if (column.key === 'special' || column.render) {
        // For special columns, try to extract sortable values
        if (key === 'position') {
          const aPos = (a as any).position;
          const bPos = (b as any).position;
          aValue = Array.isArray(aPos) ? aPos.join(', ') : aPos || '';
          bValue = Array.isArray(bPos) ? bPos.join(', ') : bPos || '';
        } else if (key === 'gamesPlayed') {
          aValue = (a as any).gamesPlayed ?? 0;
          bValue = (b as any).gamesPlayed ?? 0;
        } else if (key === 'goalsPlusMinus') {
          aValue = (a as any).goalsPlusMinus ?? 0;
          bValue = (b as any).goalsPlusMinus ?? 0;
        } else if (key === 'player') {
          aValue = `${a.playerNumber} ${a.playerName}`;
          bValue = `${b.playerNumber} ${b.playerName}`;
        } else if (key === 'PPG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).points || 0) / aGames;
          bValue = ((b as any).points || 0) / bGames;
        } else if (key === 'GPG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).goals || 0) / aGames;
          bValue = ((b as any).goals || 0) / bGames;
        } else if (key === 'TO/PG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).turnovers || 0) / aGames;
          bValue = ((b as any).turnovers || 0) / bGames;
        } else if (key === 'CTO/PG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).causedTurnovers || 0) / aGames;
          bValue = ((b as any).causedTurnovers || 0) / bGames;
        } else if (key === 'SPG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).saved || 0) / aGames;
          bValue = ((b as any).saved || 0) / bGames;
        } else if (key === 'GAPG') {
          const aGames = (a as any).gamesPlayed || 1;
          const bGames = (b as any).gamesPlayed || 1;
          aValue = ((a as any).goalsAllowed || 0) / aGames;
          bValue = ((b as any).goalsAllowed || 0) / bGames;
        } else if (key === 'S%') {
          aValue = (a as any).shotPercentage ?? 0;
          bValue = (b as any).shotPercentage ?? 0;
        } else if (key === 'W%') {
          aValue = (a as any).winPercentage ?? 0;
          bValue = (b as any).winPercentage ?? 0;
        } else if (key === 'SV') {
          aValue = (a as any).saved ?? 0;
          bValue = (b as any).saved ?? 0;
        } else {
          // Default to string comparison for other special columns
          aValue = String(a[column.key as keyof T] ?? '');
          bValue = String(b[column.key as keyof T] ?? '');
        }
      } else {
        // Regular column sorting
        aValue = a[column.key as keyof T];
        bValue = b[column.key as keyof T];
      }

      // Compare numeric values (supports numbers and numeric-like strings, including percents)
      const aNumber = toSortableNumber(aValue);
      const bNumber = toSortableNumber(bValue);
      if (aNumber !== null && bNumber !== null) {
        return direction === 'asc' ? aNumber - bNumber : bNumber - aNumber;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [players, sortConfig, columns]);

  const handleSort = (key: keyof T | 'special', idx: number) => {
    if (key === 'special') {
      // Handle special column sorting by looking at the column header
      const column = columns[idx];
      if (!column) return;

      let sortKey: string;
      if (column.header === 'Player') sortKey = 'player';
      else if (column.header === 'Position') sortKey = 'position';
      else if (column.header === 'Games Played' || column.header === 'GP') sortKey = 'gamesPlayed';
      else if (column.header === 'Goals +/-' || column.header === 'G+/-') sortKey = 'goalsPlusMinus';
      else if (column.header === 'PPG') sortKey = 'PPG';
      else if (column.header === 'GPG') sortKey = 'GPG';
      else if (column.header === 'TO/PG') sortKey = 'TO/PG';
      else if (column.header === 'CTO/PG') sortKey = 'CTO/PG';
      else if (column.header === 'SPG') sortKey = 'SPG';
      else if (column.header === 'GAPG') sortKey = 'GAPG';
      else if (column.header === 'S%') sortKey = 'S%';
      else if (column.header === 'W%') sortKey = 'W%';
      else if (column.header === 'SV') sortKey = 'SV';
      else return;

      if (onSortChange) {
        let direction: 'asc' | 'desc' = 'desc'; // Default to desc for stats
        if (sortConfig?.key === sortKey && sortConfig.direction === 'desc') {
          direction = 'asc';
        }
        onSortChange({ key: sortKey, direction });
      }
      return;
    }

    if (onSortChange) {
      let direction: 'asc' | 'desc' = 'desc'; // Default to desc for stats
      if (sortConfig?.key === String(key) && sortConfig.direction === 'desc') {
        direction = 'asc';
      }
      onSortChange({ key: String(key), direction });
    }
  };

  const getSortIcon = (key: keyof T | 'special', idx: number) => {
    let sortKey: string;

    if (key === 'special') {
      // Handle special column sorting by looking at the column header
      const column = columns[idx];
      if (!column || !column.sortable) return null;

      if (column.header === 'Player') sortKey = 'player';
      else if (column.header === 'Position') sortKey = 'position';
      else if (column.header === 'Games Played' || column.header === 'GP') sortKey = 'gamesPlayed';
      else if (column.header === 'Goals +/-' || column.header === 'G+/-') sortKey = 'goalsPlusMinus';
      else if (column.header === 'PPG') sortKey = 'PPG';
      else if (column.header === 'GPG') sortKey = 'GPG';
      else if (column.header === 'TO/PG') sortKey = 'TO/PG';
      else if (column.header === 'CTO/PG') sortKey = 'CTO/PG';
      else if (column.header === 'SPG') sortKey = 'SPG';
      else if (column.header === 'GAPG') sortKey = 'GAPG';
      else if (column.header === 'S%') sortKey = 'S%';
      else if (column.header === 'W%') sortKey = 'W%';
      else if (column.header === 'SV') sortKey = 'SV';
      else return null;
    } else {
      // Check if the column is sortable
      const column = columns[idx];
      if (column && column.sortable === false) return null;

      sortKey = String(key);
    }

    if (sortConfig?.key === sortKey) {
      return sortConfig.direction === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
    }
    return <span className="ml-1 opacity-50">↕</span>;
  };

  return (
    <div className="mb-8">
      <div className="mb-3 sm:mb-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <h2 className="text-xl sm:text-2xl font-bold text-sd-navy">{title}</h2>
          {showExport && (
            <div className="flex gap-2">
              <ExportButton
                data={sortedPlayers}
                columns={columns.map((col) => ({
                  header: col.header,
                  key: String(col.key),
                  dataKey: String(col.key),
                }))}
                title={title}
                teamName={teamName}
                seasonName={seasonName}
                size="sm"
                variant="outline"
                className="bg-sd-navy text-blue border-sd-navy hover:bg-sd-navy text-sm px-3 py-2"
                sortConfig={sortConfig}
                clientId={clientId || null}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile responsive table with fixed first column */}
      <div className="relative overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="text-center border border-[#22c55e] w-full min-w-max">
            <colgroup>
              <col style={{ width: '120px' }} />
              <col style={{ width: '120px' }} />
              {columns.slice(2).map((col, idx) => (
                <col
                  key={idx}
                  style={{
                    width:
                      col.header === 'Penalties' || col.header === 'Blocked Shots' || col.header === 'Position'
                        ? '100px'
                        : '80px',
                  }}
                />
              ))}
            </colgroup>

            <thead>
              <tr className="bg-sd-navy text-white">
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    className={`py-2 px-1 border border-[#22c55e] text-xs lg:text-sm font-semibold ${
                      col.sortable !== false ? 'cursor-pointer hover:bg-blue-700 transition-colors' : ''
                    } ${idx === 0 ? 'sticky left-0 z-10 bg-sd-navy' : ''}`}
                    onClick={() => (col.sortable !== false ? handleSort(col.key, idx) : undefined)}
                  >
                    <div className="flex items-center justify-center">
                      {col.header}
                      {col.sortable !== false && getSortIcon(col.key, idx)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="text-xs sm:text-sm bg-[#f6fff0]">
              {sortedPlayers.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-gray-500 italic">
                    No players found for this section.
                  </td>
                </tr>
              ) : (
                sortedPlayers.map((player, idx) => (
                  <tr key={player.playerId} className={idx % 2 === 1 ? 'bg-[#e6fce0]' : 'bg-white'}>
                    {columns.map((col, colIdx) => (
                      <td
                        key={colIdx}
                        className={`px-1 py-1 border border-gray-400 ${
                          colIdx === 0
                            ? 'text-left sticky left-0 z-10' + (idx % 2 === 1 ? ' bg-[#e6fce0]' : ' bg-white')
                            : 'text-center'
                        }`}
                      >
                        {col.render ? col.render(player) : String(player[col.key as keyof T] || '-')}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PlayersReportTable;
export type { PlayerBase };
